/**
 * Window scrolling helpers.
 *
 * Every layout in the app (`UserLayout`, `AdminLayout`, `AuthLayout`,
 * `MinimalLayout`) lets the document scroll — the only `overflow-y-auto`
 * containers are sidebars and dialog bodies — so the window is the correct
 * scroll target for page-level positioning.
 */

/** Height reserved for the sticky headers so the target isn't hidden under them. */
const STICKY_HEADER_OFFSET = 88;

/** Whether the user asked the OS to minimise animation. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function resolveBehavior(smooth: boolean): ScrollBehavior {
  return smooth && !prefersReducedMotion() ? 'smooth' : 'auto';
}

/**
 * Scroll the window to a given offset.
 *
 * Falls back to the two-argument `scrollTo` signature for browsers that don't
 * accept `ScrollToOptions`.
 */
function scrollWindowTo(top: number, behavior: ScrollBehavior): void {
  if (typeof window === 'undefined') return;

  try {
    window.scrollTo({ top, left: 0, behavior });
  } catch {
    window.scrollTo(0, top);
  }
}

/**
 * Jump the page back to the very top.
 *
 * Used on route changes, where an instant jump matches what a browser does on
 * a full page load. Pass `{ smooth: true }` for in-page transitions such as
 * pagination, where animating makes the position change easier to follow.
 */
export function scrollToTop(options: { smooth?: boolean } = {}): void {
  scrollWindowTo(0, resolveBehavior(options.smooth ?? false));
}

/**
 * Bring the top of `element` into view, but only when it has scrolled above
 * the viewport.
 *
 * This is the pagination case: after clicking "next page" while scrolled down
 * a long list, the new rows start off-screen above the user. Scrolling down is
 * never performed, so a list that is already fully visible stays put.
 */
export function scrollElementIntoView(
  element: HTMLElement | null,
  options: { offset?: number; smooth?: boolean } = {},
): void {
  if (typeof window === 'undefined') return;

  const { offset = STICKY_HEADER_OFFSET, smooth = true } = options;

  if (!element) {
    scrollToTop({ smooth });
    return;
  }

  const target = Math.max(0, element.getBoundingClientRect().top + window.scrollY - offset);

  // Already at or above the target: nothing above the fold to reveal.
  if (window.scrollY <= target) return;

  scrollWindowTo(target, resolveBehavior(smooth));
}
